import menuService from "@/services/menu";
import galleryService from "@/services/gallery";
// Ensure this path is correct and `meal` is properly exported from `models/menu.js`
import { meal as initialMealState } from "@/models/menu";
import moment from "moment";

const state = () => ({
  menus: [], // For calendar events
  enteredMealDates: [], // Dates that have at least one menu
  selectedMenu: null, // Holds the full menu object being viewed/edited
  foods: {}, // Categorized foods: { categoryName: [foodObjects] }
  foodCategories: [], // Array of category objects: [{id, name}]
  menuTypes: [], // Array of menu type objects: [{id, name, value, text (translated)}]
  galleryImages: [],
  loading: {
    menus: false,
    selectedMenu: false,
    foods: false,
    menuTypes: false,
    gallery: false,
    save: false,
    delete: false,
    cloneableMenus: false,
  },
  error: null,
  cloneableMenus: [], // Menus available for cloning from parent
});

const mutations = {
  SET_LOADING(state, { type, status }) {
    if (state.loading[type] !== undefined) {
      state.loading[type] = status;
    }
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  SET_MENUS(state, menus) {
    state.menus = menus.map((item) => ({
      id: item.id,
      name: item.type, // In calendar, 'name' is used for 'dorucak', 'rucak', 'vecera'
      date: item.date,
      reserve_id: item.reserve_id,
      start: new Date(`${item.date}T00:00:01`),
      end: new Date(`${item.date}T23:59:59`),
      color:
        state.menuTypes.find((mt) => mt.name === item.type)?.color || "blue",
      timed: false,
      meals: item.meals || [],
      type: item.type, // Original type: 'dorucak', 'rucak', 'vecera'
      one: item.one ? 1 : 0,
    }));
    state.enteredMealDates = [...new Set(menus.map((m) => m.date))];
  },
  ADD_MENU(state, menu) {
    const newMenuEvent = {
      id: menu.id,
      name: menu.type,
      date: menu.date,
      reserve_id: menu.reserve_id,
      start: new Date(`${menu.date}T00:00:01`),
      end: new Date(`${menu.date}T23:59:59`),
      color:
        state.menuTypes.find((mt) => mt.name === menu.type)?.color || "blue",
      timed: false,
      meals: menu.meals || [],
      type: menu.type,
      one: menu.one ? 1 : 0,
    };
    const index = state.menus.findIndex((m) => m.id === menu.id);
    if (index !== -1) {
      state.menus.splice(index, 1, newMenuEvent);
    } else {
      state.menus.push(newMenuEvent);
    }
    if (!state.enteredMealDates.includes(menu.date)) {
      state.enteredMealDates.push(menu.date);
    }
  },
  REMOVE_MENU(state, menuId) {
    const menuToRemove = state.menus.find((m) => m.id === menuId);
    state.menus = state.menus.filter((m) => m.id !== menuId);
    if (menuToRemove) {
      const remainingOnDate = state.menus.some(
        (m) => m.date === menuToRemove.date
      );
      if (!remainingOnDate) {
        state.enteredMealDates = state.enteredMealDates.filter(
          (d) => d !== menuToRemove.date
        );
      }
    }
  },
  SET_SELECTED_MENU(state, menu) {
    if (menu && menu.meals) {
      menu.meals.forEach((meal) => {
        if (!meal.type && meal.groups && meal.groups.length > 0) {
          meal.type = meal.groups.map((g) => g.group);
        } else if (!meal.type) {
          meal.type = [];
        }
      });
    }
    state.selectedMenu = menu;
  },
  CLEAR_SELECTED_MENU(state) {
    state.selectedMenu = null;
  },
  UPDATE_SELECTED_MENU_MEAL(state, mealData) {
    if (state.selectedMenu && state.selectedMenu.meals) {
      const mealIndex = state.selectedMenu.meals.findIndex(
        (m) => m.id === mealData.id
      );
      if (mealIndex !== -1) {
        state.selectedMenu.meals.splice(mealIndex, 1, mealData);
      }
    }
  },
  ADD_MEAL_TO_SELECTED_MENU(state, mealData) {
    if (state.selectedMenu) {
      if (!state.selectedMenu.meals) {
        state.selectedMenu.meals = [];
      }
      state.selectedMenu.meals.push(mealData);
    }
  },
  REMOVE_MEAL_FROM_SELECTED_MENU(state, mealId) {
    if (state.selectedMenu && state.selectedMenu.meals) {
      state.selectedMenu.meals = state.selectedMenu.meals.filter(
        (m) => m.id !== mealId
      );
    }
  },
  SET_FOODS(state, foods) {
    const categories = {};
    const foodCategories = [];
    foods.forEach((food) => {
      if (food?.category?.name) {
        const categoryName = food.category.name;
        if (!categories[categoryName]) {
          categories[categoryName] = [];
          // Add category if not already added
          if (!foodCategories.some((cat) => cat.id === food.category.id)) {
            foodCategories.push(food.category);
          }
        }
        categories[categoryName].push(food);
      }
    });
    Object.keys(categories).forEach((catName) =>
      categories[catName].sort((a, b) => a.name.localeCompare(b.name))
    );
    const sortedFoodsObject = {};
    Object.keys(categories)
      .sort()
      .forEach((name) => (sortedFoodsObject[name] = categories[name]));
    state.foods = sortedFoodsObject;
    state.foodCategories = foodCategories.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  },
  SET_MENU_TYPES(state, menuTypes) {
    // Assuming menuTypes from API is like [{id, name, color (optional)}]
    // Store raw name for 'text', translation will be handled by component/getter using it
    state.menuTypes = menuTypes;
  },
  SET_GALLERY_IMAGES(state, images) {
    state.galleryImages = images;
  },
  ADD_GALLERY_IMAGE(state, image) {
    state.galleryImages.push(image);
  },
  SET_CLONEABLE_MENUS(state, menus) {
    state.cloneableMenus = menus.map((item) => ({
      id: item.id,
      name: item.type,
      date: item.date,
      reserve_id: item.reserve_id,
      start: new Date(`${item.date}T00:00:01`),
      end: new Date(`${item.date}T23:59:59`),
      color:
        state.menuTypes.find((mt) => mt.name === item.type)?.color || "blue",
      timed: false,
      meals: item.meals || [],
      type: item.type,
      one: item.one ? 1 : 0,
    }));
  },
};

const actions = {
  async fetchMenusForMonth(
    { commit, dispatch, state },
    { startDate, endDate }
  ) {
    // Added state to access state.menuTypes
    commit("SET_LOADING", { type: "menus", status: true });
    commit("CLEAR_ERROR");
    try {
      // Ensure menu types are loaded for color coding, if not already.
      // This could be part of an initialization action as well.
      if (state.menuTypes.length === 0) {
        await dispatch("fetchMenuTypes");
      }
      const { data } = await menuService.getWebAppOnlyMenu(startDate, endDate);
      commit("SET_MENUS", data);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch menus"
      );
      console.error("Error fetching menus for month:", error);
    } finally {
      commit("SET_LOADING", { type: "menus", status: false });
    }
  },
  async fetchMenuByDate({ commit, state }, params = { date: "", type: "" }) {
    const { data } = await menuService.getMenuByDateAndType(
      params.date,
      params.type
    );
    commit("SET_SELECTED_MENU", data);
  },
  async fetchMenuById({ commit }, menuId) {
    commit("SET_LOADING", { type: "selectedMenu", status: true });
    commit("CLEAR_ERROR");
    try {
      const { data } = await menuService.getMenuById(menuId, true);
      commit("SET_SELECTED_MENU", data);
      return data; // Return data for local handling
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch menu details"
      );
      console.error("Error fetching menu by ID:", error);
      commit("SET_SELECTED_MENU", null); // Clear on error
    } finally {
      commit("SET_LOADING", { type: "selectedMenu", status: false });
    }
  },
  async saveMenu({ commit, dispatch }, menuPayload) {
    commit("SET_LOADING", { type: "save", status: true });
    commit("CLEAR_ERROR");
    try {
      // Payload normalization
      let processedPayload = { ...menuPayload };

      // Ensure 'type' is consistent with 'name' (dialog uses 'name' for v-model of type selector)
      if (processedPayload.name && !processedPayload.type) {
        processedPayload.type = processedPayload.name;
      } else if (processedPayload.type && !processedPayload.name) {
        // This case should ideally not happen if dialog keeps them in sync
        processedPayload.name = processedPayload.type;
      }

      processedPayload.meals = (processedPayload.meals || []).map((meal) => {
        const finalMeal = { ...meal };
        if (
          finalMeal.newSelectedName &&
          finalMeal.name !== finalMeal.newSelectedName
        ) {
          finalMeal.name = finalMeal.newSelectedName;
        }
        // delete finalMeal.newSelectedName; // Clean up if backend doesn't expect it
        return finalMeal;
      });

      if (!processedPayload.start && processedPayload.date) {
        processedPayload.start = new Date(`${processedPayload.date}T00:00:01`);
      }
      if (!processedPayload.end && processedPayload.date) {
        processedPayload.end = new Date(`${processedPayload.date}T23:59:59`);
      }

      let response;
      if (processedPayload.id) {
        response = await menuService.updateMenu(
          processedPayload.id,
          processedPayload
        );
      } else {
        response = await menuService.createMenu(processedPayload);
      }
      commit("ADD_MENU", response.data);
      commit("SET_SELECTED_MENU", response.data);
      return response.data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        (menuPayload.id ? "Failed to update menu" : "Failed to create menu");
      commit("SET_ERROR", {
        message: errorMessage,
        context: "menu",
        data: error.response?.data,
      });
      console.error("Error saving menu:", error);
      throw error; // Re-throw for component to handle
    } finally {
      commit("SET_LOADING", { type: "save", status: false });
    }
  },

  async cloneAndSaveMenuFromParent({ commit, dispatch, rootState }, cloneData) {
    commit("SET_LOADING", { type: "save", status: true });
    commit("CLEAR_ERROR");
    const { selected_date, selected_meals } = cloneData;
    // const restaurantId = rootState.restaurant.id;
    // const parentRestaurantId = rootState.restaurant.parent?.id;

    const payload = {
      name: selected_date.type || "rucak",
      date: selected_date.date,
      type: selected_date.type || "rucak",
      one: selected_date.one ? 1 : 0,
      meals: selected_date.meals
        .filter((meal) => selected_meals.includes(meal.id))
        .map((meal) => ({
          ...meal,
          id: null,
          menu_id: null,
          original_meal_id: meal.id,
          poredak: meal.poredak,
          can_reserve: null,
        })),
    };

    try {
      const savedMenu = await dispatch("saveMenu", payload);
      return savedMenu;
    } catch (error) {
      console.error("Error cloning and saving menu from parent:", error);
      throw error;
    } finally {
      commit("SET_LOADING", { type: "save", status: false });
    }
  },

  async fetchAndSetSelectedMenuByDateAndType(
    { commit, dispatch },
    { date, type, currentOneStatus }
  ) {
    commit("SET_LOADING", { type: "selectedMenu", status: true });
    commit("CLEAR_ERROR");
    try {
      const { data } = await menuService.getWebAppOnlyMenu(date, date);
      const menu = data.find((m) => m.type === type);
      if (menu) {
        commit("SET_SELECTED_MENU", menu);
        // If currentOneStatus is different, update the one status
        if (menu.one !== currentOneStatus) {
          await dispatch("saveMenu", { ...menu, one: currentOneStatus });
        }
      } else {
        commit("SET_SELECTED_MENU", null);
      }
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch menu"
      );
      console.error("Error fetching menu by date and type:", error);
    } finally {
      commit("SET_LOADING", { type: "selectedMenu", status: false });
    }
  },
  async deleteMenu({ commit }, menuId) {
    commit("SET_LOADING", { type: "delete", status: true });
    commit("CLEAR_ERROR");
    try {
      await menuService.deleteMenu(menuId);
      commit("REMOVE_MENU", menuId);
      commit("CLEAR_SELECTED_MENU"); // Clear if the deleted menu was selected
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete menu";
      commit("SET_ERROR", {
        message: errorMessage,
        context: "menu",
        data: error.response?.data,
      });
      console.error("Error deleting menu:", error);
      throw error; // Re-throw for component to handle
    } finally {
      commit("SET_LOADING", { type: "delete", status: false });
    }
  },
  async fetchFoods({ commit }) {
    commit("SET_LOADING", { type: "foods", status: true });
    try {
      const { data } = await menuService.getFoods();
      commit("SET_FOODS", data);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch foods"
      );
      console.error("Error fetching foods:", error);
    } finally {
      commit("SET_LOADING", { type: "foods", status: false });
    }
  },
  async fetchMenuTypes({ commit }) {
    commit("SET_LOADING", { type: "menuTypes", status: true });
    try {
      const { data } = await menuService.getMenuTypes();
      commit("SET_MENU_TYPES", data);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch menu types"
      );
      console.error("Error fetching menu types:", error);
    } finally {
      commit("SET_LOADING", { type: "menuTypes", status: false });
    }
  },
  async fetchGalleryImages({ commit }) {
    commit("SET_LOADING", { type: "gallery", status: true });
    try {
      const { data } = await galleryService.getGalleryImages();
      commit("SET_GALLERY_IMAGES", data);
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to fetch gallery images"
      );
      console.error("Error fetching gallery images:", error);
    } finally {
      commit("SET_LOADING", { type: "gallery", status: false });
    }
  },
  async uploadGalleryImage({ commit }, formData) {
    commit("SET_LOADING", { type: "gallery", status: true });
    try {
      const { data } = await galleryService.uploadGalleryImage(formData);
      commit("ADD_GALLERY_IMAGE", data);
      return data; // Return data
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to upload image"
      );
      console.error("Error uploading gallery image:", error);
      throw error;
    } finally {
      commit("SET_LOADING", { type: "gallery", status: false });
    }
  },
  async fetchCloneableMenus({ commit, rootState, dispatch, state }) {
    // Added state
    const user = rootState.user; // Assuming user state is in root. Adjust if it's in auth module e.g., rootState.auth.user
    if (
      user?.restaurants?.length &&
      user.restaurants[0].parent?.parent_menu === 1
    ) {
      commit("SET_LOADING", { type: "cloneableMenus", status: true });
      try {
        // Ensure menu types are loaded for color coding
        if (state.menuTypes.length === 0) {
          await dispatch("fetchMenuTypes");
        }
        const { data } = await menuService.getCloneableMenus({
          id: user.restaurants[0].parent_id,
          usr_id: user.id,
        });
        commit("SET_CLONEABLE_MENUS", data);
      } catch (error) {
        console.error("Error fetching cloneable menus:", error);
      } finally {
        commit("SET_LOADING", { type: "cloneableMenus", status: false });
      }
    }
  },
  // Meal specific actions within a menu context (usually part of saveMenu)
  // If direct meal manipulation is needed:
  async updateMealOrder({ commit, dispatch }, mealItem) {
    // Added dispatch
    try {
      await menuService.setOrderMeal(mealItem);
      // Optionally, if selectedMenu is affected and needs partial update without full save:
      // dispatch('updateSelectedMealLocally', mealItem); // Requires a new mutation/action
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to update meal order"
      );
      console.error("Error updating meal order:", error);
      throw error;
    }
  },
  async deleteMealFromMenu({ commit, dispatch }, { mealItem }) {
    // Removed menuId, assuming mealItem has all info
    try {
      await menuService.deleteCloneMeal(mealItem);
      commit("REMOVE_MEAL_FROM_SELECTED_MENU", mealItem.id);
      // If this action implies the parent menu should be re-saved or re-fetched:
      // dispatch('saveMenu', state.selectedMenu) or dispatch('fetchMenuById', state.selectedMenu.id)
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to delete meal"
      );
      console.error("Error deleting meal:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to delete meal";
      commit("SET_ERROR", {
        message: errorMessage,
        context: "meal",
        data: error.response?.data,
        clonedMealOnError: mealItem,
      });
      throw error;
    }
  },
  async changeReservedMealStatus({ commit, state }, payload) {
    try {
      const { data } = await menuService.changeReservedMeal(payload);
      if (state.selectedMenu && state.selectedMenu.meals) {
        const updatedMeals = state.selectedMenu.meals.map((meal) => {
          // Assuming data.data contains the updated meal or just the ID and new can_reserve status
          // And payload.item_id was the one that became reservable
          if (meal.id === payload.item_id) {
            return { ...meal, can_reserve: payload.item_id };
          }
          return { ...meal, can_reserve: null };
        });
        // A more robust way to update can_reserve based on response:
        // Find the meal that is now reservable from response (data.data.id)
        // And set its can_reserve, others to null.
        const reservableMealId = data.data.id; // Assuming API returns the ID of the meal that is now reservable
        state.selectedMenu.meals.forEach((meal) => {
          meal.can_reserve =
            meal.id === reservableMealId ? reservableMealId : null;
        });
        commit("SET_SELECTED_MENU", {
          ...state.selectedMenu,
          meals: [...state.selectedMenu.meals],
        });
      }
      return data; // Return data
    } catch (error) {
      commit(
        "SET_ERROR",
        error.response?.data?.message || "Failed to change reservation status"
      );
      console.error("Error changing reserved meal status:", error);
      throw error;
    }
  },
  // Action to initialize necessary data for the menu page
  async initializeMenuData({ dispatch }) {
    await Promise.all([
      dispatch("fetchMenuTypes"),
      dispatch("fetchFoods"),
      dispatch("fetchGalleryImages"),
      dispatch("fetchCloneableMenus"), // Fetches if conditions met
    ]);
    // Fetch initial month's menus
    const today = moment();
    await dispatch("fetchMenusForMonth", {
      startDate: today.clone().startOf("month").format("YYYY-MM-DD"),
      endDate: today.clone().endOf("month").format("YYYY-MM-DD"),
    });
  },
};

const getters = {
  // Calendar events are directly state.menus
  getMenuById: (state) => (id) => {
    return state.menus.find((menu) => menu.id === id);
  },
  // Provides menu types formatted for dropdowns (text already translated in mutation)
  getMenuTypeFilterOptions: (state, getters, rootState, rootGetters) => {
    const translatedAll = {
      text: rootGetters.translate ? rootGetters.translate("all") : "All",
      value: "all",
    };
    // Assuming components will translate `mt.text` (which is `mt.name`)
    return [
      translatedAll,
      ...state.menuTypes.map((mt) => ({ text: mt.text, value: mt.name })),
    ];
  },
  getMenuTypesForDisplay: (state) => {
    return state.menuTypes.map((item) => item.name); // Just names for checkbox group in MealCard
  },
  isLoading: (state) => (type) => {
    return type
      ? state.loading[type]
      : Object.values(state.loading).some((status) => status);
  },
  getError: (state) => {
    return state.error;
  },
  // Getter for initial meal state for dialogs
  getInitialMealState: () => () => {
    // MODIFIED: This now returns a function
    // Ensure initialMealState is not undefined here
    if (typeof initialMealState === "undefined") {
      console.error(
        "initialMealState is undefined in menu.js store. Check import from models/menu.js"
      );
      // Provide a hardcoded fallback to prevent complete failure, though this indicates a deeper issue.
      return JSON.parse(
        JSON.stringify({
          id: "",
          name: null,
          foods: [],
          description: null,
          type: [],
          price: null,
          groups: [],
          quantity: 0,
          measure: "kg",
          newSelectedName: null,
          image_id: null,
          shift_one: 0,
          shift_two: 0,
          shift_three: 0,
          one: 0,
        })
      );
    }
    return JSON.parse(JSON.stringify(initialMealState));
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
